import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import styled, { ThemeProvider } from "styled-components/macro";
import { FormattedMessage, injectIntl } from "react-intl";
import { Helmet } from "react-helmet";
import ReactGA from "react-ga4";
import { lighten, rgba } from "polished";

import { distanceGeo } from "../../utils/calc";
import { getSearchParams } from "../../utils/params";
import { scrollTop } from "../../utils/common";
import { Icon, Loading } from "../../components";
import UserNav from "../../components/UserNav";
import ResultsList from "../Search/ResultsList";

import BrandButton from "./BrandButton";
import BrandHeader from "./BrandHeader";
import SearchBar from "./SearchBar";
import SuggestionGrid from "./SuggestionGrid";
import { BABY_SUGGESTIONS, BRAND, brandBackground } from "./brand";
import { buildSearchFilters, initialFilters } from "./filters";

/* Re-skins the shared components used on this page (Button, Checkbox,
 * RangeSlider, cards) from the two brand colors, so nothing needs colors
 * passed down by prop. */
const brandTheme = theme => ({
  ...theme,
  primary: BRAND.primary,
  info: BRAND.primary,
  secondary: lighten(0.35, BRAND.primary),
});

const Wrapper = styled.div`
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

/* Full-screen background image, with the brand color as the fallback behind it
 * (and as the only paint if the image is missing or still loading). */
const Background = styled.div`
  position: fixed;
  inset: 0;
  z-index: 0;
  background-color: ${BRAND.primary};
  background-image: url("${brandBackground}");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-attachment: scroll;
`;

const Content = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

/* Results sit on an opaque sheet so the cards keep their own contrast. */
const ResultsSheet = styled.div`
  flex: 1;
  margin-top: 0.5rem;
  padding-bottom: 2rem;
  border-radius: ${props => props.theme.borderRadiusLarge}
    ${props => props.theme.borderRadiusLarge} 0 0;
  background-color: ${props => rgba(props.theme.white, 0.95)};
  backdrop-filter: blur(4px);
`;

const Notice = styled.p`
  padding: 1rem;
  margin: 2rem auto;
  width: 90%;
  max-width: 640px;
  text-align: center;
  color: ${props => props.theme.dark};
  background-color: ${props => props.theme.white};
  border-radius: ${props => props.theme.borderRadius};
  box-shadow: ${props => props.theme.boxShadowLifted};
`;

const LoadMore = styled.div`
  display: flex;
  justify-content: center;
  padding: 1rem;
`;

const PampersSearch = ({
  intl,
  isLoading,
  results,
  metadata,
  center,
  address,
  searchCourses,
  changeAddress,
}) => {
  const params = getSearchParams() || {};

  const [term, setTerm] = useState(params.term || "");
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(initialFilters);
  const [editFilters, setEditFilters] = useState(false);

  /* The grid is the empty state: it shows until a term is submitted and comes
   * back when the field is cleared. */
  const hasSearched = term !== "";

  const runSearch = useCallback(
    (searchTerm, searchPage) => {
      searchCourses(searchTerm, searchPage, center, buildSearchFilters(filters));
    },
    [searchCourses, center, filters],
  );

  // Take an address handed over by URL params, e.g. ?address=Hamburg&lat=..&lng=..
  useEffect(() => {
    const { address: paramAddress, lat, lng } = getSearchParams() || {};

    if (parseFloat(lat) && parseFloat(lng)) {
      changeAddress(paramAddress, parseFloat(lat), parseFloat(lng));
    }
  }, [changeAddress]);

  /* Center on the device position so results are local without the visitor
   * typing a place. The Address field lives inside the filter panel and only
   * mounts once that is opened, so the page has to ask for this itself.
   * Re-runs are harmless: once a position is known the guard returns early,
   * and a denied request leaves the deps unchanged. */
  useEffect(() => {
    if (address || center || !navigator.geolocation) {
      return;
    }

    navigator.geolocation.getCurrentPosition(
      ({ coords }) =>
        changeAddress(
          intl.formatMessage({ id: "components.Address.currentPosition" }),
          coords.latitude,
          coords.longitude,
        ),
      error => console.log("Couldn't get user's location", error),
      { enableHighAccuracy: false, timeout: 5000, maximumAge: Infinity },
    );
  }, [address, center, changeAddress, intl]);

  // Search whenever the term, the place or the filters change
  useEffect(() => {
    if (!hasSearched) {
      return;
    }

    runSearch(term, page);
  }, [term, page, hasSearched, runSearch]);

  const onSubmit = submitted => {
    setPage(1);
    setEditFilters(false);
    setTerm(submitted);
    scrollTop();
  };

  /* Shared by the suggestion tiles and the category pills on result cards:
   * picking a topic anywhere searches for it. */
  const onTermSelect = name => {
    if (!name) {
      return;
    }

    ReactGA.event({ category: "PampersSearch", action: name });
    onSubmit(name);
  };

  const onAddressSelect = (selected, lat, lng) => {
    changeAddress(selected, lat, lng);
    setPage(1);
  };

  const nextPage = () => {
    if (isLoading || !metadata) {
      return;
    }

    setPage(p => p + 1);
  };

  /*
   * /search/all returns no distance, only each result's address and the center
   * it searched around, so derive it here and let CourseCard's existing badge
   * render it. Providers come back without an address and so get no badge.
   *
   * The address is trimmed to postal code and city: it is only read back by
   * that badge's hover detail, where the full street line is more than it
   * needs.
   */
  const resultsWithDistance = useMemo(
    () =>
      results.map(result => {
        const address = result.address;
        const lat = address && parseFloat(address.lat);
        const lng = address && parseFloat(address.lng);

        if (!center || !lat || !lng) {
          return result;
        }

        return {
          ...result,
          distance: distanceGeo(center, { lat, lng }),
          address: { postalCode: address.postalCode, city: address.city },
        };
      }),
    [results, center],
  );

  const noResults = hasSearched && results.length < 1 && metadata && !isLoading;
  const moreResults = metadata && metadata.totalCount > results.length;

  return (
    <ThemeProvider theme={brandTheme}>
      <Wrapper>
        <Helmet>
          <title>{intl.formatMessage({ id: "pages.PampersSearch.pageTitle" })}</title>
        </Helmet>
        <Background aria-hidden="true" />
        <Content>
          <UserNav />
          <BrandHeader primaryColor={BRAND.primary} secondaryColor={BRAND.secondary} />
          <SearchBar
            isLoading={isLoading}
            term={term}
            totalCount={(metadata && metadata.totalCount) || results.length}
            hasSearched={hasSearched}
            filters={filters}
            setFilters={setFilters}
            editFilters={editFilters}
            setEditFilters={setEditFilters}
            address={address}
            center={center}
            onAddressSelect={onAddressSelect}
            onAddressClear={() => changeAddress()}
            onSubmit={onSubmit}
          />
          {hasSearched ? (
            <ResultsSheet>
              <ResultsList
                results={resultsWithDistance}
                onCategoryClick={category => onTermSelect(category && category.name)}
                hasMore={false}
                term={term}
              />
              {moreResults && (
                <LoadMore>
                  {isLoading ? (
                    <Loading />
                  ) : (
                    <BrandButton onClick={nextPage} round>
                      <FormattedMessage id="pages.Search.loadMore" />
                      <Icon name="chevron-up" direction="down" spaced="left" />
                    </BrandButton>
                  )}
                </LoadMore>
              )}
              {noResults && (
                <Notice>
                  <FormattedMessage id="pages.Search.noResults" />
                </Notice>
              )}
            </ResultsSheet>
          ) : (
            <SuggestionGrid
              suggestions={BABY_SUGGESTIONS}
              onSelect={onTermSelect}
              accentColor={BRAND.primary}
              headingColor={BRAND.primary}
            />
          )}
        </Content>
      </Wrapper>
    </ThemeProvider>
  );
};

PampersSearch.propTypes = {
  intl: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  results: PropTypes.array,
  metadata: PropTypes.object,
  center: PropTypes.object,
  address: PropTypes.string,

  /* Dispatchable actions */
  searchCourses: PropTypes.func.isRequired,
  changeAddress: PropTypes.func.isRequired,
};

PampersSearch.defaultProps = {
  results: [],
  metadata: null,
  center: null,
  address: null,
};

export default injectIntl(PampersSearch);
